import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TaskTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TaskTypeActions from "../../../actions/taskTypes";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./TaskTypesPage.scss";

interface TaskTypesPageProps {
    formFilter: IFormFilter;
    taskTypes: Dtos.TaskType[];
    loadingTaskTypes: boolean;
    loadTaskTypesSuccess: boolean;
    loadTaskTypesFailure: boolean;
    loadTaskTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearTaskTypes: TaskTypeActions.ITaskTypeClearActionCreator;
    loadTaskTypes: TaskTypeActions.ITaskTypeLoadActionCreator;
    filter: TaskTypeActions.ITaskTypeFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const TaskTypesPage: FC<TaskTypesPageProps> = ({
    formFilter,
    taskTypes,
    loadingTaskTypes,
    loadTaskTypesSuccess,
    loadTaskTypesFailure,
    loadTaskTypesErrorCode,
    permissions,
    pageSize,
    clearTaskTypes,
    loadTaskTypes,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTaskTypes = !loadingTaskTypes && loadTaskTypesSuccess;


    // Load task types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(TaskTypesBreadcrumbs());

            loadTaskTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        TaskTypesBreadcrumbs,
        loadTaskTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTaskTypesFailure) {
            navigate(`/error/${loadTaskTypesErrorCode}`);
        }
    }, [loadTaskTypesFailure, loadTaskTypesErrorCode, navigate]);

    // Cleanup effect to clear the task types on component unmount
    useEffect(() => {
        return () => {
            clearTaskTypes();
        };
    }, [clearTaskTypes]);

    const createTable = useMemo(() => {
        if (!taskTypes || taskTypes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no task types to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = taskTypes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={taskTypes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : taskTypes.length
                    }
                    pageSize={showPagination ? pageSize : taskTypes.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no task types to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/settings/qa/tasktypes/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        taskTypes,
        PAGE_SIZE_OPTIONS,
        pageSize,
        DEFAULT_PAGE_SIZE,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Task Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TaskTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/qa/tasktypes/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTaskTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTaskTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showTaskTypes, createTable]);

    return (
        <QALookupsLayout
            loading={loadingTaskTypes}
            permission={Dtos.Permission.TaskTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TaskTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.taskTypes.formFilter,
        taskTypes: state.taskTypes.formFilteredData,
        loadingTaskTypes:
            state.taskTypes.loadState &&
            state.taskTypes.loadState.status == RequestState.Pending,
        loadTaskTypesSuccess:
            state.taskTypes.loadState &&
            state.taskTypes.loadState.status == RequestState.Success,
        loadTaskTypesFailure:
            state.taskTypes.loadState &&
            state.taskTypes.loadState.status == RequestState.Failure,
        loadTaskTypesErrorCode: state.taskTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<TaskTypesPageProps> => {
    return {
        loadTaskTypes: bindActionCreators(
            TaskTypeActions.LoadTaskType,
            dispatch
        ),
        clearTaskTypes: bindActionCreators(TaskTypeActions.Clear, dispatch),
        filter: bindActionCreators(TaskTypeActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskTypesPage);
